import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import {
  INTENSITY_OVER_TIME_QUERY,
  IntensityOverTimeData,
  IntensityOverTimePayload,
  IntensityTrend,
} from "../../../../../../legacy/queries/Intensity";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";

interface useIntensityOverTimeProps {
  hgId: string;
  productIds: number[];
  lookBackYears: number;
}

/**
 * Options for transforming intensity over time data to intensity trend.
 * @typedef {Object} IntensityTrendOptions
 * @property {Array<IntensityOverTimeData>} [intensityOverTimeData] - Array of intensity over time data.
 * @property {number} lookBackYears - Number of years to look back for filtering data.
 */
interface IntensityTrendOptions {
  intensityOverTimeData?: Array<IntensityOverTimeData>;
  lookBackYears: number;
}

/**
 * Filters intensity over time data by the look back years.
 * @param {IntensityTrendOptions} options - Options for filtering intensity over time data.
 * @returns {Array<IntensityOverTimeData>} - Filtered intensity over time data.
 */
const getIntensityOverTimeFilteredYears = (options: IntensityTrendOptions) => {
  const lookBackDate = new Date();

  lookBackDate.setDate(1);
  lookBackDate.setHours(0, 0, 0, 0);
  lookBackDate.setMonth(lookBackDate.getMonth() - 12 * options.lookBackYears);

  return options.intensityOverTimeData?.filter(({ trend }) => {
    const trendDate = new Date(trend.year, trend.month - 1, 1);

    return trendDate >= lookBackDate;
  });
};

/**
 * Transforms intensity over time data to intensity trend.
 * @param {IntensityTrendOptions} options - Options for transforming intensity over time data.
 * @returns {Array<IntensityTrend>} - Transformed intensity trend data.
 */
const transformIntensityOverTimeToIntensityTrend = (
  options: IntensityTrendOptions
): Array<IntensityTrend> => {
  const intensityOverTimeFilteredYears =
    getIntensityOverTimeFilteredYears(options);

  return (
    intensityOverTimeFilteredYears?.map(({ productName, trend }) => ({
      trend,
      product: {
        id: Number.POSITIVE_INFINITY,
        name: productName,
        description: "",
      },
    })) || []
  );
};

export const useIntensityOverTime = (props: useIntensityOverTimeProps) => {
  const { data, loading: isLoading } = useQuery<IntensityOverTimePayload>(
    INTENSITY_OVER_TIME_QUERY,
    {
      variables: {
        hgId: props.hgId,
        productIds: props.productIds,
      },
      skip: props.productIds.length === 0,
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
    }
  );

  const intensityOverTimeData = useMemo<Array<IntensityTrend>>(
    () =>
      transformIntensityOverTimeToIntensityTrend({
        intensityOverTimeData: data?.intensityOverTime,
        lookBackYears: props.lookBackYears,
      }),
    [props.lookBackYears, data?.intensityOverTime]
  );

  return {
    intensityOverTimeData,
    isLoading,
  };
};
